import { SingleComboboxOption } from '@sr/ui-library';
import React from 'react';
import * as FlagSVGs from 'react-flags-select';
import { COUNTRIES_DATA } from '../../../constant/CountryData';

interface CountryFlagProps {
  code: string;
}

/**
 * react-flags-select uses Pascal-cased named exports (e.g. AU → "Au").
 * Returns null for codes with no flag — callers fall back to name-only rendering.
 */
export const CountryFlag = ({ code }: CountryFlagProps) => {
  const flagKey =
    code.length >= 2 ? code[0].toUpperCase() + code[1].toLowerCase() : '';
  // Double-cast needed: the namespace import's `default` export (ReactFlagsSelect)
  // breaks the plain Record index signature without going through `unknown` first.
  const FlagComponent = (
    FlagSVGs as unknown as Record<
      string,
      React.ComponentType<{ className?: string }>
    >
  )[flagKey];

  return FlagComponent ? <FlagComponent className="h-4 w-6 shrink-0" /> : null;
};

/**
 * Same country list as old SR.app which was being controlled by react-flags-select
 * "default" and non-2-char keys are excluded.
 */
export const ACCEPTABLE_COUNTRY_CODES = new Set(
  Object.keys(FlagSVGs as unknown as Record<string, unknown>)
    .filter((k) => k !== 'default' && k.length === 2)
    .map((k) => k.toUpperCase()),
);

/**
 * Country options for form fields (Business Number Country, Tax Residency).
 *
 * Filtered to only the countries present in react-flags-select so that v3
 * matches v2's country count exactly. v2 passes all COUNTRIES_DATA IDs to
 * ReactFlagsSelect, which silently drops codes it doesn't recognise — so
 * the effective list is the intersection of COUNTRIES_DATA and ACCEPTABLE_COUNTRY_CODES.
 *
 * The resulting list matches v2's effective country count exactly.
 */
export const getAllCountryComboboxOptions = (
  option: Partial<SingleComboboxOption>,
): SingleComboboxOption[] =>
  COUNTRIES_DATA.filter((country) =>
    ACCEPTABLE_COUNTRY_CODES.has(country.Id),
  ).map((country) => ({
    ...option,
    element: (
      <span className="flex items-center gap-2">
        <CountryFlag code={country.Id} />
        {country.Name}
      </span>
    ),
    label: country.Name,
    value: country.Id,
  }));

/**
 * Convenience export for tests/consumers that need the full precomputed list.
 * (All entries have flag coverage and are aligned with react-flags-select.)
 */
export const ALL_COUNTRY_OPTIONS = getAllCountryComboboxOptions({});
